/**
 * @file ReceiptItemForm.tsx
 * @description Панель «Добавить строку» приёмки (phase19.13 этап 0; фокус и
 * свёрнутая панель — phase19.24 этап 1, шаг 1; срок «мягко», скан ячейки и
 * повтор фасовки — phase19.22, W20, W14-9).
 *
 * Позиция — фасовка (SKU): первое поле — скан артикула или этикетки склада с
 * «Подбором» (`SkuSelectField`). Дат по умолчанию нет: придуманный срок хуже
 * пустого (ТР ТС 022) — срок считается от даты изготовления по карточке или
 * виду товара, пустой подсвечивается, но строку добавить можно.
 *
 * Фасовка уже в документе — выбор «перейти к строке / новая строка с другим
 * сроком» (W20: одна строка = один срок = одна партия).
 *
 * Фокус в «Количество» переводит ЭФФЕКТ по выбранной фасовке, а не колбэк
 * поля (иначе фокус улетал в `body`); на `<lg` панель свёрнута до скана.
 *
 * phase19.31 (SC51): в режиме кладовщика «Цены продажи» нет ни в форме, ни в
 * запросе — режим решает страница (`showSalePrice`).
 */

import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ROW_HINT } from "./document-texts";
import {
  buildReceiptItemPayload,
  emptyLot,
  type ReceiptItemPayload,
} from "./receipt-item-payload";
import { ReceiptDuplicateChoice } from "./ReceiptDuplicateChoice";
import { ReceiptItemLotFields, type ReceiptItemLotValue } from "./ReceiptItemLotFields";
import { SkuSelectField } from "./SkuSelectField";
import { useSkuExpiryPolicy } from "./useSkuExpiryPolicy";
import { useWarehouseBins } from "./useWarehouseBins";
import { WmsFieldLabel } from "./WmsFieldLabel";
import type { WmsPickerSku } from "./WmsSkuPickerDialog";
import { Button } from "../ui/button";
import { asideFieldsClass } from "../ui/aside-fields";
import { DocumentAside } from "../ui/DocumentAside";
import { ensureFieldsVisibleAboveBar } from "../ui/sticky-bar";
import { formatPickerSkuName, type GoodsReceiptItemDto } from "../../lib/api-wms";
import { qtyInputProps, skuCountUnit, validateQty } from "../../lib/qty-input";
import { formatMonthYear, withAutoExpiry } from "../../lib/receipt-expiry";

// Тип тела строки живёт в `receipt-item-payload.ts`; реэкспорт — для старых импортов.
export type { ReceiptItemPayload };

interface Props {
  /** Строки документа — повтор фасовки и подпись «срок 03.2027». */
  existingItems?: GoodsReceiptItemDto[];
  /** Уже добавленные skuId (старый контракт; `existingItems` его перекрывает). */
  alreadyAddedIds?: string[];
  onGoToExisting?: (skuId: string) => void;
  /** Склад документа — ячейки и резолвер скана (W14). */
  warehouseId: string;
  submitting: boolean;
  /** true — позиция добавлена, форма сбрасывается. */
  onSubmit: (payload: ReceiptItemPayload) => Promise<boolean>;
  /** Показывать и отправлять цену продажи; кладовщику — нет (SC51, W11). */
  showSalePrice?: boolean;
}

export function ReceiptItemForm({
  existingItems,
  alreadyAddedIds,
  onGoToExisting,
  warehouseId,
  submitting,
  onSubmit,
  showSalePrice = true,
}: Props) {
  const [sku, setSku] = useState<WmsPickerSku | null>(null);
  const [duplicate, setDuplicate] = useState<WmsPickerSku | null>(null);
  const [quantity, setQuantity] = useState("");
  const [lot, setLot] = useState<ReceiptItemLotValue>(() => emptyLot(""));
  const [qtyError, setQtyError] = useState<string | null>(null);
  const qtyRef = useRef<HTMLInputElement>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);
  const bins = useWarehouseBins(warehouseId);
  const { policy } = useSkuExpiryPolicy(sku?.productId);
  const countUnit = skuCountUnit(sku);
  const addedIds = existingItems ? existingItems.map((it) => it.skuId) : (alreadyAddedIds ?? []);

  useEffect(() => {
    if (!sku) return;
    qtyRef.current?.focus();
    ensureFieldsVisibleAboveBar(fieldsRef.current, qtyRef.current);
  }, [sku]);

  // Сменилась фасовка или пришла её политика срока — автосрок (не ручной)
  // пересчитываем или очищаем: срок чужой фасовки на новой строке — ошибка.
  useEffect(() => {
    setLot((prev) => withAutoExpiry(prev, policy.shelfLifeDays));
  }, [policy.shelfLifeDays, sku?.id]);

  const hint = !sku ? ROW_HINT.sku : !quantity.trim() ? ROW_HINT.quantity : null;

  /** «срок 03.2027» существующей строки — чем она отличается. */
  function existingNote(skuId: string): string {
    const row = existingItems?.find((it) => it.skuId === skuId);
    const exp = row?.lot?.expiresAt ?? row?.expiresAt;
    return exp ? `срок ${formatMonthYear(exp)}` : "без срока";
  }

  async function handleSubmit() {
    if (!sku || !quantity.trim()) return;
    const message = validateQty(quantity, countUnit, { allowZero: false });
    if (message) {
      setQtyError(message);
      return;
    }
    setQtyError(null);
    const ok = await onSubmit(buildReceiptItemPayload(sku.id, quantity, lot, policy, { includeSalePrice: showSalePrice }));
    if (!ok) return;
    setSku(null);
    setQuantity("");
    // Ячейку НЕ сбрасываем: приёмку обычно раскладывают в одно место подряд.
    setLot(emptyLot(lot.binId));
  }

  return (
    <DocumentAside
      title="Добавить строку"
      chrome="lg"
      titleClassName="hidden lg:flex"
      footerClassName={sku ? undefined : "hidden lg:flex"}
      footer={
        <>
          <Button onClick={() => void handleSubmit()} disabled={!!hint || submitting}>
            <Plus className="size-4" />
            <span className="ml-1.5">Добавить позицию</span>
          </Button>
          {hint ? <p className="text-xs text-on-surface-variant">{hint}</p> : null}
        </>
      }
    >
      <WmsFieldLabel label="Фасовка (артикул или скан)" className="hidden lg:block">
        <SkuSelectField
          value={sku}
          onChange={(next) => {
            setDuplicate(null);
            setSku(next);
          }}
          nextFieldRef={qtyRef}
          alreadyAddedIds={addedIds}
          onGoToExisting={onGoToExisting}
          onExisting={existingItems ? setDuplicate : undefined}
        />
      </WmsFieldLabel>

      {duplicate && !sku ? (
        <ReceiptDuplicateChoice
          skuName={formatPickerSkuName(duplicate)}
          existingNote={existingNote(duplicate.id)}
          onGoTo={() => {
            onGoToExisting?.(duplicate.id);
            setDuplicate(null);
          }}
          onNewLine={() => {
            setSku(duplicate);
            setDuplicate(null);
          }}
        />
      ) : null}

      <div ref={fieldsRef} className={asideFieldsClass(!!sku)}>
        <WmsFieldLabel label={`Количество, ${countUnit}`}>
          <input
            ref={qtyRef}
            type="text"
            value={quantity}
            onChange={(e) => {
              setQuantity(e.target.value);
              setQtyError(null);
            }}
            inputMode={qtyInputProps(countUnit).inputMode}
            placeholder="0"
            disabled={!sku}
            className="min-h-[var(--control-h,3rem)] w-full rounded-lg bg-surface-lowest px-3 text-[length:var(--fs-field,1rem)] tabular-nums ring-ghost focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:bg-surface-low disabled:text-on-surface-variant"
          />
        </WmsFieldLabel>
        {qtyError ? <p className="text-xs text-destructive">{qtyError}</p> : null}

        {/* Б15: пока фасовка не выбрана, поля строки неактивны (подсказка —
            под кнопкой «Добавить позицию»): даты, введённые в пустую форму,
            не применялись к уже добавленной строке и пропадали. */}
        <ReceiptItemLotFields
          value={lot}
          onChange={setLot}
          warehouseId={warehouseId}
          bins={bins}
          expiryPolicy={policy}
          showSalePrice={showSalePrice}
          disabled={!sku}
        />
      </div>
    </DocumentAside>
  );
}
